// React
import { memo, useRef } from 'react';

// Third-party
import { Button, Space } from 'antd';
import classNames from 'classnames';

// Shared components
import { StyledModal, WithLoading } from 'common/components';

// Wizard components
import WizardFooter from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/components/WizardFooter';
import WizardSteps from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/components/WizardSteps';
import {
  OnlineGridSection,
  WizardGrid,
  WizardGridAction,
} from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/components/OnlineGridModal';
import { LoadTemplateModal } from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/components/ProductGridQuestionStep';
import { WizardModalGroup } from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/components/WizardModalGroup';
import { WizardZeroCostSuccessModal } from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/components/WizardPayment';
import RecaptureModal from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/components/RecaptureModal';

// Wizard hooks / context
import { useWizard } from '../context/useWizard';
import useOnlineGrid from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/hooks/useOnlineGrid';
import { useWizardSubmission } from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/hooks/useWizardSubmission';
import useStepTransition from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/hooks/useStepTransition';
import { usePreloadNextStepImages } from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/hooks/usePreloadNextStepImages';

// Config
import { getStepComponent } from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/config/stepComponents';
import { MODAL_TYPES } from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/config/stepModalConstants';
import { onlineGridModalProps } from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/constants/wizardModalProps';

// Styles
import '../styles/Wizard.less';

const WizardModal = ({
  visible,
  onClose: onCloseWizardModal,
  wizardFormId,
  showWizardContent,
}) => {
  const {
    state,
    currentStep,
    nextStepKey,
    handleClose: handleCloseModalStep,
    actions,
    closeModal,
    openModal,
    isModalOpen,
  } = useWizard();

  usePreloadNextStepImages(nextStepKey);

  const WizardStep = getStepComponent(currentStep?.type);

  const stepContentRef = useRef(null);
  const stepDirection = useStepTransition(state.currentStep, stepContentRef);

  const {
    gridRef,
    selectedRow,
    productModal,
    isSaving,
    isValidating,
    memberId,
    unitUpcOptions,
    onSelectionChange,
    handleClearSelectedRow,
    handleAddNewProduct,
    handleRecaptureRow,
    handleAddRecaptureProducts,
    handleCloseRecaptureModal,
    recaptureModalVisible,
    recaptureTargetRow,
    handleCellValueChanged,
    handleEditProduct,
    handleDeleteProduct,
    handleSubmitProduct,
    handleCloseProductModal,
    handleSave,
    handleValidate,
    handleAddRecapture,
  } = useOnlineGrid(state, actions);

  const {
    handleSaveAndPrepareSubmit,
    handleSubmissionContinue,
    hasErrors,
    isSubmitting,
    passedProductCount,
    passedProduct,
    wizardPaymentInfo,
  } = useWizardSubmission({
    state,
    actions,
    openModal,
    closeModal,
    memberId,
    handleValidate,
    // Don't close the whole wizard here — that would unmount
    // WizardModalGroup before it can render ZERO_COST_SUCCESS.
    // WizardZeroCostSuccessModal's own Close button calls onCloseWizard
    // once the user has actually seen the success message.
    onZeroCostSuccess: () => {
      closeModal(MODAL_TYPES.ONLINE_GRID);
    },
    gridRef,
  });

  const totalProducts = state?.products?.length ?? 0;

  const handleCloseWizard = () => {
    onCloseWizardModal?.();
    handleCloseModalStep();
  };

  const handleSubmitPassedProducts = async () => {};

  const handleCloseSubmissionResult = () => {
    closeModal(MODAL_TYPES.ACCEPT_SUBMISSION);
  };

  return (
    <>
      {/* Main Wizard Modal */}
      <StyledModal
        title='Product Upload Wizard'
        visible={visible && showWizardContent}
        onCancel={handleCloseWizard}
        width={1180}
        zIndex={999}
        footer={
          <WizardFooter
            onValidateWizardForm={handleValidate}
            onCloseWizard={handleCloseWizard}
          />
        }
        className='product-upload-wizard-modal'
        maskClosable={false}
        destroyOnClose
        keyboard={false}
      >
        <div className='wizard-layout'>
          <div className='wizard-sidebar scroller--y'>
            <WizardSteps />
          </div>
          <div ref={stepContentRef} className='wizard-content-wrapper scroller--y'>
            <div
              key={state.currentStep}
              className={classNames('wizard-step-container', {
                'wizard-step-container--intro': currentStep?.type === 'intro',
                [`wizard-step-container--enter-${stepDirection}`]:
                  stepDirection,
              })}
            >
              {WizardStep ? (
                <WizardStep
                  step={currentStep}
                  onValidateWizardForm={handleValidate}
                />
              ) : (
                <div className='wizard-step-error'>
                  Unknown step type: {currentStep?.type}
                </div>
              )}
            </div>
          </div>
        </div>
      </StyledModal>

      {/* Online Grid Modal */}
      {isModalOpen(MODAL_TYPES.ONLINE_GRID) && (
        <StyledModal
          {...onlineGridModalProps}
          zIndex={1010}
          visible
          onCancel={() => {
            closeModal(MODAL_TYPES.ONLINE_GRID);
          }}
          footer={
            <Space>
              <Button
                onClick={() => closeModal(MODAL_TYPES.ONLINE_GRID)}
                disabled={isValidating || isSaving}
              >
                Cancel
              </Button>
              <Button
                type='primary'
                onClick={handleSave}
                loading={isSaving}
                disabled={isValidating || state.isGridLoading || !totalProducts}
              >
                Save
              </Button>
              <Button
                loading={isSubmitting || state.isGridLoading}
                disabled={
                  isValidating ||
                  isSaving ||
                  state.isGridLoading ||
                  !totalProducts
                }
                type='primary'
                onClick={async () => {
                  await handleSaveAndPrepareSubmit({ wizardFormId });
                }}
              >
                Submit
              </Button>
            </Space>
          }
        >
          <OnlineGridSection
            onAddNewProduct={handleAddNewProduct}
            onFindProducts={handleAddRecapture}
            disabled={isValidating || isSaving || state.isGridLoading}
            wizardGridAction={
              <WizardGridAction
                onAddNewProduct={handleAddNewProduct}
                onFindProducts={handleAddRecapture}
                onEditProduct={handleEditProduct}
                onDeleteProduct={handleDeleteProduct}
                selectedRow={selectedRow}
                totalProducts={totalProducts}
                disabled={isValidating || isSaving || state.isGridLoading}
              />
            }
            wizardGrid={
              <WithLoading
                loading={state.isGridLoading || isValidating || isSaving}
                tip='Processing...'
              >
                <WizardGrid
                  gridRef={gridRef}
                  memberId={memberId}
                  unitUpcOptions={unitUpcOptions}
                  onSelectionChange={onSelectionChange}
                  onCellValueChanged={handleCellValueChanged}
                  onRecaptureRow={handleRecaptureRow}
                  onClearSelectedRow={handleClearSelectedRow}
                />
              </WithLoading>
            }
          />
        </StyledModal>
      )}

      {/* Load Template Modal */}
      {isModalOpen(MODAL_TYPES.LOAD_TEMPLATE_UPLOAD) && (
        <LoadTemplateModal
          onValidateWizardForm={handleValidate}
          visible={true}
          memberId={memberId}
          onCancel={() => closeModal(MODAL_TYPES.LOAD_TEMPLATE_UPLOAD)}
          gridRef={gridRef}
        />
      )}

      <RecaptureModal
        visible={recaptureModalVisible}
        onCancel={handleCloseRecaptureModal}
        onAddProducts={handleAddRecaptureProducts}
        existingIds={state.products.map((p) => p.productUnitId).filter(Boolean)}
        isSingleSelect={!!recaptureTargetRow}
      />

      {isModalOpen(MODAL_TYPES.ZERO_COST_SUCCESS) && (
        <WizardZeroCostSuccessModal
          visible
          onCancel={() => closeModal(MODAL_TYPES.ZERO_COST_SUCCESS)}
          onCloseWizard={handleCloseWizard}
        />
      )}

      <WizardModalGroup
        productModal={productModal}
        isSubmitting={isSubmitting}
        wizardFormId={wizardFormId}
        wizardPaymentInfo={wizardPaymentInfo}
        hasErrors={hasErrors}
        passedProductCount={passedProductCount}
        passedProduct={passedProduct}
        onCloseProductModal={handleCloseProductModal}
        onCloseWizard={handleCloseWizard}
        onSubmitProduct={handleSubmitProduct}
        onSubmissionContinue={handleSubmissionContinue}
        onSubmitPassedProducts={handleSubmitPassedProducts}
        onReviewProducts={() => {
          closeModal(MODAL_TYPES.ACCEPT_SUBMISSION);
        }}
        onAcceptCancel={handleCloseSubmissionResult}
      />
    </>
  );
};

export default memo(WizardModal);
